import { useContext, useEffect, useState } from 'react';
import { Stack, TextField } from '@mui/material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { ICreateSurveyQuestionGroup, ISurveyQuestionGroup } from '../../../shared/services/survey/survey.model';

interface Props {
  open: boolean;
  onClose: () => void;
  successHandler: () => void;
  existingGroup?: ISurveyQuestionGroup;
}

export const SurveyQuestionGroupDialog = ({ open, onClose, successHandler, existingGroup }: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const isEditMode = !!existingGroup;

  useEffect(() => {
    if (open && existingGroup) {
      setName(existingGroup.name);
      setDescription(existingGroup.description ?? '');
    } else if (open) {
      setName('');
      setDescription('');
    }
  }, [open, existingGroup]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      const payload: ICreateSurveyQuestionGroup = {
        name: name.trim(),
        description: description.trim() || undefined,
      };
      if (isEditMode && existingGroup) {
        await surveyAdminService.updateGroup(existingGroup.id, payload);
      } else {
        await surveyAdminService.createGroup(payload);
      }
      userNotificationsContext.openSnackbar({
        open: true,
        message: isEditMode ? 'Question set updated' : 'Question set created',
        backgroundColor: 'primary.dark',
      });
      successHandler();
      onClose();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'An error occurred, please try again', backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Question Set' : 'Create Question Set'}
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!name.trim()}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          label='Question set name'
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          required
        />
        <TextField
          label='Description (optional)'
          value={description}
          onChange={e => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
        />
      </Stack>
    </DialogWrapper>
  );
};
